import React from 'react'
import { css, cx } from '@emotion/css'
import {
  theme,
  ThemeColors
} from '@contrast-security-inc/design-system-foundations'
import { LoadingMaskThermometer } from '../loading-masks'
import { thermometerCss, ThermometerValueRendererProps } from './thermometer'

const intervalCss = css`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
`

const intervalCssCache = new Map<string, string>()

function getIntervalCss(color: ThemeColors) {
  if (!intervalCssCache.has(color)) {
    intervalCssCache.set(
      color,
      cx(
        intervalCss,
        css`
          background-color: ${theme.color[color]};
          &:only-child,
          &:not(:nth-last-child(2)) {
            border-right: 1px solid ${theme.color.white};
          }
        `
      )
    )
  }

  return intervalCssCache.get(color)
}

function sum(values: number[]) {
  return values.reduce((acc, value) => acc + value, 0)
}

interface SegmentedThermometerProps {
  /**
   * The values that make up this thermometer component
   */
  values: number[]

  /**
   * Returns the key in the theme colors for the value at
   * the given index
   */
  getColor: (index: number) => ThemeColors

  /**
   * Render callback for the value at a given index.
   * This function can return null.
   */
  renderValue: (props: {
    index: number
    width: string
    propsGetter: (
      props?: any
    ) => Pick<ThermometerValueRendererProps, 'className' | 'key'>
  }) => React.ReactElement<any> | null

  testId?: string
}

const Wrapper = ({
  children,
  testId
}: {
  children: React.ReactNode
  testId?: string
}) => {
  return (
    <div className={thermometerCss} data-testid={testId} data-e2e={testId}>
      {children}
    </div>
  )
}

export function SegmentedThermometer({
  values,
  getColor,
  renderValue,
  testId
}: SegmentedThermometerProps) {
  const max = sum(values)
  return (
    <Wrapper testId={testId}>
      {values.map((value, index) =>
        renderValue({
          index,
          width: `${(value / max) * 100}%`,
          propsGetter: (props = {}) => {
            return {
              ...props,
              className: getIntervalCss(getColor(index)) || '',
              key: index
            }
          }
        })
      )}
    </Wrapper>
  )
}

export const SegmentedThermometerLoadingView = () => {
  return (
    <Wrapper>
      <LoadingMaskThermometer />
    </Wrapper>
  )
}
